import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

export const SUB_PAGES = [
  {
    to: "/age-calculator/chronological-date-difference",
    title: "Chronological & Date Difference",
    desc: "Exact age gap between any two dates — for exams, forms, records.",
    emoji: "📅",
  },
  {
    to: "/age-calculator/gestational-pregnancy",
    title: "Gestational / Pregnancy",
    desc: "LMP-based gestational age, trimester and Estimated Due Date.",
    emoji: "🤰",
  },
  {
    to: "/age-calculator/pet-dog-cat",
    title: "Pet Age (Dog & Cat)",
    desc: "Convert your dog or cat's age into equivalent human years.",
    emoji: "🐾",
  },
  {
    to: "/age-calculator/retirement-pension",
    title: "Retirement & Pension",
    desc: "State pension eligibility, retirement year and RMD milestones.",
    emoji: "🏖️",
  },
  {
    to: "/age-calculator/biological-health-bmi",
    title: "Biological & Health Age",
    desc: "Metabolic, heart & BMI-based biological age fitness score.",
    emoji: "❤️‍🔥",
  },
  {
    to: "/age-calculator/lifestyle-lunar-face",
    title: "Lunar & Alternative",
    desc: "Chinese lunar age, zodiac sign and skin maturation timeline.",
    emoji: "🌙",
  },
  {
    to: "/age-calculator/upsc-eligibility",
    title: "UPSC Age Eligibility",
    desc: "Check UPSC CSE age limits with category-wise relaxations (SC/ST/OBC/PwBD).",
    emoji: "🎯",
  },
  {
    to: "/age-calculator/fpsc-ppsc-jobs",
    title: "FPSC & PPSC Jobs",
    desc: "Pakistan government job age eligibility with in-service relaxation.",
    emoji: "🇵🇰",
  },
  {
    to: "/age-calculator/school-admission",
    title: "School Admission Age",
    desc: "Nursery, KG & Class 1 admission cut-off age by board and state.",
    emoji: "🎒",
  },
  {
    to: "/age-calculator/relationship-age-gap",
    title: "Relationship Age Gap",
    desc: "Half-plus-seven rule and healthy age difference between partners.",
    emoji: "💞",
  },
  {
    to: "/age-calculator/military-bharti",
    title: "Army / Military Bharti Age",
    desc: "Indian Army, Navy, Air Force & Agniveer age eligibility checker.",
    emoji: "🪖",
  },
  {
    to: "/age-calculator/passport-visa-form",
    title: "Passport & Visa Age",
    desc: "Minor/adult passport rules and visa DOB calculation as-on date.",
    emoji: "🛂",
  },
  {
    to: "/age-calculator/retirement-countdown",
    title: "Retirement Countdown",
    desc: "Live ticker to your retirement date with years-months-days left.",
    emoji: "⏳",
  },
  {
    to: "/age-calculator/pet-age-converter",
    title: "Pet Age Converter",
    desc: "Advanced dog & cat age in human years by breed and size.",
    emoji: "🐶",
  },
  {
    to: "/age-calculator/milestone-birthday",
    title: "Milestone Birthday",
    desc: "Countdown to your next big birthday — 18, 21, 30, 40, 50, 60.",
    emoji: "🎂",
  },
  {
    to: "/age-calculator/chinese-lunar-age",
    title: "Chinese Lunar Age",
    desc: "Traditional East Asian lunar age with zodiac animal and element.",
    emoji: "🐉",
  },
] as const;

export function SubHubGrid({ excludePath }: { excludePath?: string }) {
  const items = SUB_PAGES.filter((p) => p.to !== excludePath);
  return (
    <section className="mx-auto max-w-6xl px-4 py-12">
      <h2 className="text-2xl md:text-3xl font-bold mb-2">Specialized Advanced Calculators Available on DrHint</h2>
      <p className="text-muted-foreground mb-6">Purpose-built age tools — each one runs 100% locally in your browser for instant answers.</p>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((it) => (
          <Link
            key={it.to}
            to={it.to}
            className="group rounded-2xl border border-border bg-card p-5 hover:border-primary hover:shadow-lg transition-all"
          >
            <div className="text-3xl mb-2">{it.emoji}</div>
            <div className="font-semibold text-base group-hover:text-primary">{it.title}</div>
            <div className="mt-1 text-sm text-muted-foreground">{it.desc}</div>
          </Link>
        ))}
      </div>
      <div className="mt-8 text-center">
        <Link to="/age-calculator" className="text-primary underline underline-offset-4 font-semibold">
          ← Back to the main Age Calculator hub
        </Link>
      </div>
    </section>
  );
}

export function ContentSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mx-auto max-w-4xl px-4 py-8 prose prose-sm md:prose-base dark:prose-invert max-w-none">
      <h2 className="text-2xl md:text-3xl font-bold mb-4">{title}</h2>
      {children}
    </section>
  );
}

export function FaqBlock({ faqs }: { faqs: { q: string; a: string }[] }) {
  return (
    <section className="mx-auto max-w-4xl px-4 py-10">
      <h2 className="text-2xl md:text-3xl font-bold mb-5">Frequently Asked Questions</h2>
      <div className="space-y-3">
        {faqs.map((f) => (
          <details key={f.q} className="rounded-xl border border-border bg-card p-4 group">
            <summary className="cursor-pointer font-semibold list-none flex justify-between items-center">
              <span>{f.q}</span>
              <span className="ml-2 text-primary group-open:rotate-45 transition-transform">+</span>
            </summary>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{f.a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

export function buildFaqLd(faqs: { q: string; a: string }[]) {
  return {
    type: "application/ld+json" as const,
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    }),
  };
}

export function buildBreadcrumbLd(trail: { name: string; url: string }[]) {
  return {
    type: "application/ld+json" as const,
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: trail.map((t, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: t.name,
        item: t.url,
      })),
    }),
  };
}

/** CLS-safe wrapper that reserves vertical space so animated result cards
 *  never cause layout shift on mobile. */
export function ReservedResultsArea({ children, minHeight = 220 }: { children: React.ReactNode; minHeight?: number }) {
  return (
    <div style={{ minHeight, contain: "layout" }} className="mt-6">
      {children}
    </div>
  );
}

export function ResultCard({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div
      className="rounded-2xl border border-border bg-gradient-to-br from-card to-muted/40 p-5 shadow-sm animate-in fade-in slide-in-from-bottom-2 duration-500"
      style={{ minHeight: 96, contain: "layout" }}
    >
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl md:text-3xl font-black tabular-nums text-primary">{value}</div>
      {sub ? <div className="mt-1 text-xs text-muted-foreground">{sub}</div> : null}
    </div>
  );
}

export function inputCls() {
  return "w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary";
}